import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getEmailSends,
  getEmailSplitTests,
  getEmailSummary,
  messageFor as cvceMessageFor,
  type EmailSend,
  type SplitArm,
} from "@/modules/cvce_marketing/api";
import { armLabel, formatFraction, languageLabel } from "@/modules/cvce_marketing/format";

import { funnelsKey, getFunnels, messageFor } from "./api";
import { ALL_FUNNELS, FunnelControls, funnelLabel, selectedFunnel, useControls } from "./controls";
import { ALL_SENDS_CAPTION, ALL_SENDS_LABEL, armOrders, emailSummaryLine, funnelEmailKey, funnelEmailQuery, sendsById } from "./email-format";
import { formatCount } from "./format";
import { OrdersStamp } from "./OrderFigures";
import { formatMoney, formatOrders } from "./stats-format";

const numeric = { meta: { align: "right" as const } };

const SEND_COLUMNS: ColumnDef<EmailSend, unknown>[] = [
  { id: "send", header: "Send", accessorFn: (row) => row.broadcast_name ?? row.broadcast_id },
  { id: "date", header: "Sent", accessorFn: (row) => row.send_date },
  { id: "lang", header: "Lang", accessorFn: (row) => languageLabel(row.language) },
  { id: "recipients", header: "Recips", accessorFn: (row) => formatCount(row.recipients), ...numeric },
  { id: "open", header: "Open", accessorFn: (row) => formatFraction(row.open_rate), ...numeric },
  { id: "ctr", header: "CTR", accessorFn: (row) => formatFraction(row.click_rate), ...numeric },
  { id: "orders", header: "Orders", accessorFn: (row) => formatOrders(row.orders_attributed), ...numeric },
  { id: "revenue", header: "Revenue", accessorFn: (row) => formatMoney(row.revenue_attributed_gbp), ...numeric },
];

type ArmRow = SplitArm & { group: string; orders: number | null; revenue: number | null };

const ARM_COLUMNS: ColumnDef<ArmRow, unknown>[] = [
  { id: "group", header: "Send", accessorFn: (row) => row.group },
  { id: "arm", header: "Arm", accessorFn: (row) => armLabel(row) },
  { id: "recipients", header: "Recips", accessorFn: (row) => formatCount(row.recipients), ...numeric },
  { id: "ctr", header: "CTR", accessorFn: (row) => formatFraction(row.click_rate), ...numeric },
  { id: "orders", header: "Orders", accessorFn: (row) => formatOrders(row.orders), ...numeric },
  { id: "revenue", header: "Revenue", accessorFn: (row) => formatMoney(row.revenue), ...numeric },
];

/**
 * One funnel's email (Precision Funnel C9 R2): the sends stamped to it and each
 * split-test arm, with the orders and revenue cvce credits to each send. It reads
 * the same cvce endpoints as the CVCE Marketing Email tab, cut by `funnel`, so the
 * two modules always agree on a send's orders.
 */
export function EmailTab({ areaName }: { areaName: string }) {
  const controls = useControls();
  const funnels = useQuery({ queryKey: funnelsKey(controls.days), queryFn: () => getFunnels(controls.days) });
  const list = funnels.data?.data.funnels ?? [];
  // All by default (FN-1). cvce can cut to one funnel or to none, so All is every send in the
  // window, stamped to a funnel or not; the tab says so. cvce sums; the portal adds nothing (I-4).
  const selected = selectedFunnel(controls.funnel, list, true) as string;
  const all = selected === ALL_FUNNELS;
  const q = funnelEmailQuery(all ? null : selected, controls.days);
  // The funnel list settles the choice first, so a deep link never fires an unfiltered read it then discards.
  const enabled = funnels.isSuccess;

  const summary = useQuery({ queryKey: funnelEmailKey("summary", q), queryFn: () => getEmailSummary(q), enabled });
  const sends = useQuery({ queryKey: funnelEmailKey("sends", q), queryFn: () => getEmailSends(q), enabled });
  const split = useQuery({ queryKey: funnelEmailKey("split", q), queryFn: () => getEmailSplitTests(q), enabled });

  if (funnels.isError) {
    return (
      <Alert>
        <AlertTitle>Landing page figures unavailable</AlertTitle>
        <AlertDescription>{messageFor(funnels.error, areaName)}</AlertDescription>
      </Alert>
    );
  }
  if (funnels.isPending) return <Skeleton className="h-96 w-full" />;

  const failed = summary.error ?? sends.error ?? split.error;
  const sendRows = sends.data?.data.sends ?? [];
  const byId = sendsById(sendRows);
  const armRows: ArmRow[] = (split.data?.data.groups ?? []).flatMap((g) =>
    g.arms.map((arm) => ({ ...arm, group: g.campaign_key, ...armOrders(arm, byId) })),
  );

  return (
    <div className="flex flex-col gap-4">
      <FunnelControls controls={controls} funnels={list} selected={selected} allowAll allLabel={ALL_SENDS_LABEL} />
      {failed ? (
        <Alert>
          <AlertTitle>Email figures unavailable</AlertTitle>
          <AlertDescription>{cvceMessageFor(failed, areaName)}</AlertDescription>
        </Alert>
      ) : (
        <>
          {summary.data && (
            <p className="text-base text-ks-ink" aria-live="polite">
              {emailSummaryLine(summary.data.data.summary, funnelLabel(selected, ALL_SENDS_LABEL), controls.days, all)}
            </p>
          )}
          {all && <p className="text-sm text-muted-foreground">{ALL_SENDS_CAPTION}</p>}
          <OrdersStamp
            dataAsOf={sends.data?.meta?.data_as_of}
            caption="Orders are credited to the last email clicked in the 7 days before the order, the same rule Content Engine uses."
          />
          <DataTable
            columns={SEND_COLUMNS}
            rows={sendRows}
            pending={sends.isPending}
            page={{ pageIndex: 0, pageSize: Math.max(sendRows.length, 1), rowCount: sendRows.length }}
            onPageChange={() => {}}
            emptyMessage={all ? "No sends in this window." : "No sends stamped to this funnel in this window."}
            caption={`Email sends, ${funnelLabel(selected, ALL_SENDS_LABEL).toLowerCase()}, last ${controls.days} days`}
          />
          <DataTable
            columns={ARM_COLUMNS}
            rows={armRows}
            pending={split.isPending}
            page={{ pageIndex: 0, pageSize: Math.max(armRows.length, 1), rowCount: armRows.length }}
            onPageChange={() => {}}
            emptyMessage={all ? "No split test ran on a send in this window." : "No split test ran on this funnel's sends in this window."}
            caption={`Split-test arms, ${funnelLabel(selected, ALL_SENDS_LABEL).toLowerCase()}, last ${controls.days} days`}
          />
        </>
      )}
    </div>
  );
}
